import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Sparkles, Globe, Heart, Brain, ChevronRight } from 'lucide-react';

export default function LifeCraft() {
  const handleBuyNow = () => {
    // Phase 3 will replace this with Stripe Checkout session creation
    alert('Stripe Checkout integration coming soon. For now, this is a placeholder. Thank you for your interest in LifeCraft!');
  };

  return (
    <div className="min-h-screen bg-slate-950 text-gray-200">
      {/* Top nav — back to OmniAlera */}
      <nav className="border-b border-purple-500/20 bg-slate-950/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-medium">OmniAlera</span>
          </Link>
          <span className="text-xs text-gray-500 hidden sm:block">Game 01 of 40 · LifeCraft</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Decorative animated gradient — TODO: replace this entire div with hero screenshot when assets ready */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        <motion.div
          className="max-w-6xl mx-auto px-6 pt-20 pb-16 text-center relative"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-300 text-sm mb-6">
            <Sparkles className="w-4 h-4" />
            <span>Now in early access</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
              Live a thousand lives,
            </span>
            <br />
            <span className="text-white">never the same one twice</span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-3xl mx-auto">
            AI-powered life simulation across 67 cultures
          </p>

          {/* Primary CTA */}
          <div className="flex flex-col items-center gap-4 mb-6">
            <button
              onClick={handleBuyNow}
              className="px-10 py-5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-lg font-semibold shadow-lg shadow-purple-500/50 hover:shadow-purple-500/70 hover:scale-105 transition-all"
            >
              Buy Now — $6.99
            </button>
            <a
              href="#features"
              className="text-purple-300 hover:text-purple-200 transition-colors text-sm inline-flex items-center gap-1"
            >
              Learn more
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          {/* Social proof */}
          <p className="text-gray-500 text-sm">
            Join early players from Lagos, Nairobi, Manila, and Mumbai
          </p>

          {/* Trust signals */}
          <p className="text-gray-600 text-xs mt-6 max-w-md mx-auto">
            18+ only · One-time purchase, no subscription · 14-day refund window
            <br />
            By purchasing you agree to our{' '}
            <Link to="/lifecraft/privacy" className="text-purple-400 hover:text-purple-300 underline">
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link to="/lifecraft/terms" className="text-purple-400 hover:text-purple-300 underline">
              Terms of Service
            </Link>
            .
          </p>
        </motion.div>
      </section>

      {/* HERO SCREENSHOT PLACEHOLDER — TODO: replace with actual gameplay screenshot */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="aspect-video rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/40 via-slate-900 to-pink-900/40 flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(139,92,246,0.15),transparent_70%)]" />
          <p className="text-gray-500 text-sm relative z-10">Gameplay screenshot placeholder</p>
        </div>
      </section>

      {/* FEATURES SECTION */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-20">
        <motion.h2
          className="text-3xl md:text-5xl font-bold text-center mb-4"
          initial={{ opacity: 1, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Every choice shapes who you become
          </span>
        </motion.h2>
        <p className="text-gray-400 text-center text-lg mb-16 max-w-2xl mx-auto">
          LifeCraft simulates a full life across eight chapters, from childhood to legacy.
          Every decision changes who you become.
        </p>

        <div className="grid md:grid-cols-2 gap-8">
          <FeatureCard
            icon={<Globe className="w-8 h-8" />}
            title="67 cultural contexts"
            description="Live in Lagos, Manila, Mumbai, Mexico City, or any of 67 cultural backgrounds. Decisions are scored against the values and pressures of where you grew up."
          />
          <FeatureCard
            icon={<Brain className="w-8 h-8" />}
            title="AI-generated narrative"
            description="No two lives are the same. Anthropic's Claude writes your story chapter by chapter based on the choices you make and the person you're becoming."
          />
          <FeatureCard
            icon={<Heart className="w-8 h-8" />}
            title="Real consequences"
            description="Choices in chapter 2 echo into chapter 7. Relationships compound. Mistakes haunt. Triumphs feel earned."
          />
          <FeatureCard
            icon={<Sparkles className="w-8 h-8" />}
            title="Voice narration"
            description="Hear your story in 11 accent families covering 67+ cultures, from Nigerian-English to Filipino-English to British RP."
          />
        </div>
      </section>

      {/* WHAT YOU GET / PRICING SECTION */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <motion.h2
          className="text-3xl md:text-4xl font-bold mb-6 text-white"
          initial={{ opacity: 1, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          One life. One price. Yours forever.
        </motion.h2>
        <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
          $6.99 unlocks chapters 4 through 8 of a single LifeCraft save and the legacy generation feature for that life. No subscription. No microtransactions.
        </p>

        <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-3xl p-8 md:p-12 max-w-2xl mx-auto">
          <p className="text-5xl md:text-6xl font-bold text-white mb-2">$6.99</p>
          <p className="text-gray-400 mb-8">Per life · One-time payment</p>

          <ul className="text-left space-y-3 mb-10 max-w-md mx-auto">
            <FeatureLine>Chapters 4–8 unlocked for one save</FeatureLine>
            <FeatureLine>Full legacy generation for that life</FeatureLine>
            <FeatureLine>All 67 cultural contexts</FeatureLine>
            <FeatureLine>All 11 voice accent families</FeatureLine>
            <FeatureLine>14-day refund window (before chapter 4)</FeatureLine>
          </ul>

          <button
            onClick={handleBuyNow}
            className="px-10 py-5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-lg font-semibold shadow-lg shadow-purple-500/50 hover:shadow-purple-500/70 hover:scale-105 transition-all"
          >
            Buy Now — $6.99
          </button>

          <p className="text-gray-500 text-xs mt-6">
            Payment processed securely by Stripe. We never see or store your card details.
          </p>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="max-w-3xl mx-auto px-6 py-20">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-10 text-center">Common questions</h2>
        <div className="space-y-6">
          <FAQ q="What's a 'life'?" a="One full playthrough — eight chapters, ages 0 through ~80, ending with a legacy generation that summarizes who your character became. A life takes about 2–4 hours of gameplay." />
          <FAQ q="Does my $6.99 cover multiple saves?" a="No. Each unlock applies to one save game. If you start a new life, that one needs its own unlock. We chose this model so you can fully commit to one character without paying a subscription." />
          <FAQ q="Can I get a refund?" a="Yes, within 14 days of purchase, provided you haven't progressed past chapter 4 in that save. After chapter 4, the unlock is considered substantially used." />
          <FAQ q="What devices does it run on?" a="LifeCraft runs on Android phones and any modern web browser. Active testing on Samsung tablets confirmed. Each install holds its own save, so your purchase unlocks the save on the device where you bought it. iOS and cross-device save sync are both on the roadmap." />
          <FAQ q="Is this AI-generated content safe?" a="Narratives are generated by Anthropic's Claude AI. They are fictional and should not be relied on as professional advice. AI-generated content is clearly disclosed in our Terms of Service." />
        </div>
      </section>

      {/* FOOTER PADDING */}
      <div className="pb-20" />
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <motion.div
      className="p-6 rounded-2xl border border-purple-500/20 bg-purple-500/5 hover:border-purple-500/40 hover:bg-purple-500/10 transition-all"
      initial={{ opacity: 1, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-purple-300 mb-4">{icon}</div>
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-gray-400 leading-relaxed">{description}</p>
    </motion.div>
  );
}

function FeatureLine({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3 text-gray-300">
      <span className="text-purple-400 mt-1">✓</span>
      <span>{children}</span>
    </li>
  );
}

function FAQ({ q, a }: { q: string; a: string }) {
  return (
    <div className="border border-purple-500/20 rounded-xl p-5 bg-purple-500/5">
      <p className="font-semibold text-white mb-2">{q}</p>
      <p className="text-gray-400 leading-relaxed">{a}</p>
    </div>
  );
}
